import React, { useRef, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { TextField, Button, InputAdornment, Chip, Stack, Avatar } from '@mui/material';
import ImageSearchIcon from '@mui/icons-material/ImageSearch';
import FileUploadIcon from '@mui/icons-material/FileUpload';
import Grid from '@mui/material/Grid2';
import { toSnakeCase } from "../utils/general_utils";

const textFieldStyles = {
  width: '100%',
  '& .MuiOutlinedInput-input': { 
    padding: '8px 10px 9px 0px'
  }, 
  '& .MuiOutlinedInput-root': {
    padding: '0',
  },
};

const buttonStyles = {
  backgroundColor: 'rgba(25, 118, 210, 0.04)',
  color: '#003B8D',
  border: 'none',
  textTransform: 'none',
  borderRadius: '4px 4px',
  '&:hover': {
    backgroundColor: 'rgba(25, 118, 210, 0.04)',
  },
  fontWeight: '400',
  fontSize: '16px',
  boxShadow: 'none',
};

const chipStyles = {
  borderRadius: 0,
  border: '2px solid #ABABB5',
  color: '#111', 
};

// 'image' | 'pdf' | 'both' | 'excel' | 'csv' | 'sheet'
const FileInput = forwardRef(({
  onFileSelect,
  fileType = 'image',
  placeholder = "Subir imagen de firma",
  initialFile = null,
  buttonText = "Añadir archivo",
  sizeBtnGrid = 3
}, ref) => {
  const fileInputRef = useRef(null);
  const sizeInputGrid = 12 - sizeBtnGrid;
  const [fileName, setFileName] = useState('');
  const [filePreview, setFilePreview] = useState('');

  const OpenFile = () => {
    fileInputRef.current?.click();
  };

  const resetFileInput = () => {
    setFileName('');
    setFilePreview('');
    if (fileInputRef.current) fileInputRef.current.value = '';
    // manda null (o lo que uses) para avisar que no hay archivo
    onFileSelect?.(null);
  };

  // Exponer método imperativo al padre
  useImperativeHandle(ref, () => ({
    clear: resetFileInput
  }));

  const handleDeleteFile = () => {
    resetFileInput();
  };

  useEffect(() => {
    if (initialFile && initialFile.archivo){
      fileHandler(initialFile.archivo);
    }
  }, [initialFile]);

  const fileHandler = (archivo) => {
    const isImage = archivo.type?.startsWith('image/');
    const isPDF   = archivo.type === 'application/pdf';

    // Excel / CSV (múltiples mimes según navegador)
    const excelMimes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel'
    ];
    const csvMimes = ['text/csv', 'application/vnd.ms-excel'];

    const isExcel = excelMimes.includes(archivo.type);
    const isCSV   = csvMimes.includes(archivo.type) || archivo.name?.toLowerCase().endsWith('.csv');

    const allow =
      (fileType === 'image' && isImage) ||
      (fileType === 'pdf' && isPDF) ||
      (fileType === 'both' && (isImage || isPDF)) ||
      (fileType === 'excel' && isExcel) ||
      (fileType === 'csv' && isCSV) ||
      (fileType === 'sheet' && (isExcel || isCSV));

    if (!allow) {
      alert('Tipo de archivo no permitido.');
      resetFileInput();
      return;
    }

    if (isImage) {
      const img = new Image();
      img.src = URL.createObjectURL(archivo);
      img.onload = () => {
        if (img.width > 256 || img.height > 256) {
          alert('La imagen no puede superar 256x256 píxeles.');
          resetFileInput();
        } else {
          setFileName(archivo.name);
          onFileSelect?.(archivo);

          const reader = new FileReader();
          reader.onload = (e) => setFilePreview(e.target.result);
          reader.readAsDataURL(archivo);
        }
      };
    } else if (isPDF || isExcel || isCSV) {
      if (archivo.size > 25 * 1024 * 1024) {
        alert('El archivo no puede superar los 25 MB.');
        resetFileInput();
      } else {
        setFileName(archivo.name);
        setFilePreview(null);
        onFileSelect?.(archivo);
      }
    }
  };

  const fileChange = (event) => {
    const archivo = event.target.files?.[0];
    if (archivo) fileHandler(archivo);
  };

  const accept =
    fileType === 'image'
      ? 'image/jpeg, image/png'
      : fileType === 'pdf'
      ? 'application/pdf'
      : fileType === 'both'
      ? 'image/jpeg, image/png, application/pdf'
      : fileType === 'excel'
      ? '.xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel'
      : fileType === 'csv'
      ? '.csv, text/csv, application/vnd.ms-excel'
      : fileType === 'sheet'
      ? '.xlsx, .csv, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.ms-excel, text/csv'
      : '';

  return (
    <>
      <Grid container spacing={2}>
        <Grid size={sizeInputGrid}>
          <TextField
            test_id={toSnakeCase("file_upload")}
            variant="outlined"
            placeholder={placeholder}
            value={fileName}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{marginLeft: "10px"}}>
                  <ImageSearchIcon sx={{color: 'black'}}/>
                </InputAdornment>
              ),
              readOnly: true,
            }}
            onClick={OpenFile}
            readOnly
            sx={textFieldStyles}
          />
        </Grid>
        <Grid size={sizeBtnGrid}>
          <Button
            test_id={toSnakeCase(buttonText)}
            fullWidth
            variant="text"
            color="primary"
            startIcon={<FileUploadIcon />}
            sx={buttonStyles}
            onClick={OpenFile}
          >
            {buttonText}
          </Button>
        </Grid>
      </Grid>

      <input
        test_id={toSnakeCase("file")}
        type="file"
        ref={fileInputRef}
        accept={accept}
        style={{ display: 'none' }}
        onChange={fileChange}
      />

      {fileName && (
        <Stack direction="row" spacing={1} mt={2}>
          <Chip
            test_id={toSnakeCase("file_chip")}
            avatar={
              filePreview ? (
                <Avatar src={filePreview} alt={fileName} sx={{ borderRadius: 0 }} />
              ) : (
                <Avatar>
                  <FileUploadIcon />
                </Avatar>
              )
            }
            label={fileName}
            onDelete={handleDeleteFile}
            sx={chipStyles}
          />
        </Stack>
      )}
    </>
  );
});

export default FileInput;
